/**
 * Enregistrement des sessions animateur (PostHog, hébergé dans l'UE) — finalité « amélioration du produit ».
 *
 * Règles (enquête pilotage 19/10/2026, choix Victorien du 20/10) :
 * - JAMAIS sans accord explicite (`ub_consent` p1) : le SDK n'est même pas téléchargé avant ;
 * - JAMAIS sur les téléphones des joueurs (`/gamepad`, `/reward`) ni sur un écran d'aéroport ou de
 *   client sous contrat (décidé par l'appelant, cf. `consentScope.ts`) ;
 * - saisies masquées (défaut PostHog) + tout élément `.ub-mask` (pseudos des joueurs affichés sur la TV) ;
 * - l'intérieur des jeux est filmé dans la même vidéo (`recordCrossOriginIframes`, réglage identique
 *   dans le jeu) : le jeu ne démarre PostHog que si la console le lui demande (`analytics` dans
 *   useWebViewPlugin), avec le même identifiant.
 * Sans clé (`POSTHOG_PUBLIC_KEY` vide), tout reste inerte.
 */
import type { PostHog } from 'posthog-js';

/**
 * Clé du projet PostHog UE « Unboared » : publique par nature (elle part dans chaque requête du
 * navigateur, comme la clé Firebase), donc écrite ici plutôt qu'en secret de CI. Vide = inerte.
 * `VITE_POSTHOG_KEY` ne sert qu'aux essais en local.
 */
const POSTHOG_PUBLIC_KEY = 'phc_ydAjxMB4CzoEwoo3UxpGDhEM2Rpyn2ezqxGtYNzWoEdC';
export const POSTHOG_KEY: string = import.meta.env.VITE_POSTHOG_KEY || POSTHOG_PUBLIC_KEY;
export const POSTHOG_HOST = 'https://eu.i.posthog.com';
export const MASK_SELECTOR = '.ub-mask';
/** Émis quand l'enregistrement démarre ou s'arrête : la console renvoie alors l'état au jeu en cours. */
export const ANALYTICS_CHANGE_EVENT = 'ub-analytics-change';
const notify = () => window.dispatchEvent(new Event(ANALYTICS_CHANGE_EVENT));

export type AnalyticsIdentity = { uid: string; clientID?: string; venue?: string; mode?: string; lang?: string };

/** Options partagées avec les jeux (même réglage des deux côtés, cf. doc PostHog « iframes »). */
export const POSTHOG_OPTIONS = {
    api_host: POSTHOG_HOST,
    ui_host: 'https://eu.posthog.com',
    person_profiles: 'identified_only' as const,
    capture_pageview: 'history_change' as const,
    autocapture: true,
    disable_surveys: true,
    enable_recording_console_log: false,
    session_recording: {
        recordCrossOriginIframes: true,
        maskAllInputs: true,
        maskTextSelector: MASK_SELECTOR,
    },
};

let instance: PostHog | null = null;
let loading: Promise<PostHog | null> | null = null;
/**
 * État VOULU, relu après chaque attente : un refus (ou une sortie du périmètre) qui arrive pendant le
 * téléchargement du SDK (wifi de bar lent) ne doit jamais être écrasé par le démarrage en cours.
 */
let wanted: AnalyticsIdentity | null = null;
let identifiedUid: string | null = null;
/** Retrait demandé avant la fin du téléchargement : l'oubli sera fait dès que le SDK sera là. */
let forgetPending = false;

/** Efface les traces PostHog du navigateur (cookie `ph_*` sur .unboared.com et stockage local). */
function purgeBrowserStorage(): void {
    try {
        Object.keys(window.localStorage)
            .filter((k) => k.startsWith('ph_') || k.startsWith('__ph_'))
            .forEach((k) => window.localStorage.removeItem(k));
    } catch {
        // stockage bloqué (navigation privée) : rien à effacer
    }
    document.cookie
        .split('; ')
        .map((c) => c.split('=')[0])
        .filter((name) => name.startsWith('ph_'))
        .forEach((name) => {
            document.cookie = `${name}=; Max-Age=0; Path=/`;
            document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.unboared.com`;
        });
}

function applyWanted(ph: PostHog): void {
    if (!wanted) {
        ph.stopSessionRecording();
        if (!ph.has_opted_out_capturing()) ph.opt_out_capturing();
        notify();
        return;
    }
    // Changement de compte sur le même navigateur : jamais deux personnes dans la même session.
    if (identifiedUid && identifiedUid !== wanted.uid) ph.reset();
    // Après un retrait (`forget`), la persistance était passée en mémoire : on la rétablit.
    if (ph.config.persistence === 'memory') ph.set_config({ persistence: 'localStorage+cookie' });
    if (ph.has_opted_out_capturing()) ph.opt_in_capturing();
    ph.startSessionRecording();
    ph.identify(wanted.uid, {
        clientID: wanted.clientID,
        venue: wanted.venue,
        mode: wanted.mode,
        lang: wanted.lang,
    });
    identifiedUid = wanted.uid;
    notify();
}

/** Retrait de l'accord : identifiant oublié, enregistreur arrêté, traces du navigateur effacées. */
function forget(ph: PostHog): void {
    // `reset()` AVANT `opt_out_capturing()` : reset efface aussi l'état « refusé » (vérifié le 20/10,
    // posthog-js 1.436.1) — dans l'autre ordre, l'enregistrement repartait après un refus.
    ph.reset();
    identifiedUid = null;
    ph.set_config({ persistence: 'memory' });
    purgeBrowserStorage();
    // En DERNIER : l'effacement ci-dessus supprime aussi la marque « refusé » de PostHog (vérifié le
    // 20/10) ; `applyWanted` la repose (seule trace laissée : ce refus) et arrête l'enregistreur.
    applyWanted(ph);
}

export function isProductAnalyticsRunning(): boolean {
    return !!wanted && !!instance && !instance.has_opted_out_capturing();
}

export async function startProductAnalytics(identity: AnalyticsIdentity): Promise<PostHog | null> {
    if (!POSTHOG_KEY) return null;
    wanted = identity;
    forgetPending = false;
    if (!loading) {
        loading = import('posthog-js')
            .then(({ default: posthog }) => {
                posthog.init(POSTHOG_KEY, POSTHOG_OPTIONS);
                instance = posthog;
                return posthog;
            })
            .catch(() => {
                loading = null; // réseau coupé / bloqueur de pub : on réessaiera au prochain accord
                return null;
            });
    }
    const ph = await loading;
    if (!ph) return null;
    if (!wanted && forgetPending) {
        forgetPending = false;
        forget(ph);
    } else {
        applyWanted(ph); // relit `wanted` : un arrêt arrivé pendant l'attente gagne
    }
    return ph;
}

/**
 * Plus rien ne part et l'enregistreur s'arrête. `forget` (retrait de l'accord, déconnexion) oublie en
 * plus l'identifiant et efface les traces PostHog du navigateur ; sans `forget` (page hors périmètre,
 * ex. client d'aéroport), simple pause.
 */
export function stopProductAnalytics({ forget: forgetIdentity = false }: { forget?: boolean } = {}): void {
    wanted = null;
    if (!instance) {
        if (forgetIdentity) {
            purgeBrowserStorage();
            if (loading) forgetPending = true; // un démarrage en cours oubliera tout à son arrivée
        }
        return;
    }
    if (forgetIdentity) forget(instance);
    else applyWanted(instance);
}

/** Identifiants à transmettre au jeu pour qu'il rejoigne la même personne et la même session. */
export function getBridgeIds(): { distinctId: string; sessionId: string } | null {
    if (!isProductAnalyticsRunning() || !instance) return null;
    return { distinctId: instance.get_distinct_id(), sessionId: instance.get_session_id() };
}

/**
 * Message `analytics` envoyé au jeu (écran TV seulement, cf. useWebViewPlugin). Le jeu ne démarre
 * PostHog que sur `enabled: true` venu de la console, avec la même clé, la même personne et la même
 * session : l'intérieur du jeu s'ajoute à la vidéo de la console (`recordCrossOriginIframes`).
 */
export type AnalyticsBridgeMessage =
    | { action: 'analytics'; data: { enabled: false } }
    | {
          action: 'analytics';
          data: { enabled: true; key: string; host: string; distinctId: string; sessionId: string };
      };

export function analyticsBridgeMessage(): AnalyticsBridgeMessage {
    const ids = getBridgeIds();
    if (!ids || !POSTHOG_KEY) return { action: 'analytics', data: { enabled: false } };
    return { action: 'analytics', data: { enabled: true, key: POSTHOG_KEY, host: POSTHOG_HOST, ...ids } };
}
